
import React from 'react';
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip as RechartsTooltip, ResponsiveContainer } from 'recharts';
import { Card, CardHeader, CardTitle, CardContent } from '@/components/ui/card';
import ChartLegend from '@/components/charts/ChartLegend';

interface SmartMeterPerformanceChartProps {
  chartData: any[];
  hiddenLines: string[];
  onLegendClick: (dataKey: string) => void;
  telemetryType: 'energy' | 'gas' | 'water';
}

const SmartMeterPerformanceChart: React.FC<SmartMeterPerformanceChartProps> = ({
  chartData,
  hiddenLines,
  onLegendClick,
  telemetryType
}) => {
  // Get the appropriate consumption label based on telemetry type
  const getConsumptionLabel = () => {
    switch (telemetryType) {
      case 'gas':
        return 'Gas Consumption (m³/h)';
      case 'water':
        return 'Water Consumption (L/h)';
      default:
        return 'Power Consumption (W)';
    }
  };

  // Get the appropriate utility color
  const getUtilityColor = () => {
    switch (telemetryType) {
      case 'gas':
        return '#f59e0b'; // amber
      case 'water':
        return '#0ea5e9'; // sky blue
      default:
        return '#10b981'; // green
    }
  };

  // Get chart title based on telemetry type
  const getChartTitle = () => {
    return telemetryType === 'energy' ? 'Average Power (W)' : `Average ${telemetryType.charAt(0).toUpperCase() + telemetryType.slice(1)} Consumption`;
  };

  return (
    <Card>
      <CardHeader className="pb-2">
        <CardTitle className="text-lg">{getChartTitle()}</CardTitle>
      </CardHeader>
      <CardContent>
        <div className="h-80">
          <ResponsiveContainer width="100%" height="100%">
            <LineChart
              data={chartData}
              margin={{
                top: 5,
                right: 30,
                left: 20,
                bottom: 5,
              }}
            >
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis dataKey="name" />
              <YAxis />
              <RechartsTooltip 
                formatter={(value, name) => {
                  if (name === 'averagePower') return [`${value} W`, 'Average Power'];
                  if (name === 'averageConsumption') {
                    if (telemetryType === 'gas') return [`${value} m³/h`, 'Average Consumption'];
                    if (telemetryType === 'water') return [`${value} L/h`, 'Average Consumption'];
                  }
                  if (name === 'totalReadings') return [`${value}`, 'Reading Count'];
                  return [value, name];
                }}
              />
              {telemetryType === 'energy' ? (
                <Line 
                  type="monotone" 
                  dataKey="averagePower" 
                  name="Average Power (W)" 
                  stroke={getUtilityColor()} 
                  activeDot={{ r: 8 }} 
                  strokeWidth={2}
                  hide={hiddenLines.includes('averagePower')}
                />
              ) : (
                <Line 
                  type="monotone" 
                  dataKey="averageConsumption" 
                  name={getConsumptionLabel()} 
                  stroke={getUtilityColor()} 
                  activeDot={{ r: 8 }} 
                  strokeWidth={2}
                  hide={hiddenLines.includes('averageConsumption')}
                />
              )}
              <Line 
                type="monotone" 
                dataKey="totalReadings" 
                name="Readings (count)" 
                stroke="#3b82f6" 
                strokeDasharray="5 5"
                hide={hiddenLines.includes('totalReadings')}
              />
            </LineChart>
          </ResponsiveContainer>
        </div>
      </CardContent>
    </Card>
  );
};

export default SmartMeterPerformanceChart;
